/**
 * MSK-fixed datetime formatting for the SPA.
 *
 * The backend stores every timestamp as naive Moscow time (see
 * `app.clock.now_naive_msk`) and serialises with `.isoformat()`, which
 * produces a string WITHOUT a `Z` or `+HH:MM` suffix. JavaScript's
 * `new Date(...)` parses such naive strings as the *browser's* local
 * timezone, and `.toLocaleString()` then renders in the same local
 * zone — so a user in Baku saw 16:30 while a user in Moscow saw 15:30
 * for the same DB row. We always want the value the operator sees in
 * the database, in 24-hour Moscow time.
 *
 * Resolution rules:
 *   - String with explicit timezone (`Z` or `+HH:MM`) → trust the
 *     wire format, render in MSK regardless.
 *   - Naive string (no tz) → treat as MSK and render in MSK. The
 *     two together produce the same digits, which is the goal.
 *
 * All format outputs are 24-hour, `YYYY-MM-DD HH:MM[:SS]`. Locale
 * agnostic so it can't surprise an operator who reads the page in a
 * different language.
 */

const MSK_TZ = "Europe/Moscow";


function _parse(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const hasTz = /(Z|[+-]\d{2}:?\d{2})$/.test(iso);
  const d = hasTz ? new Date(iso) : new Date(iso + "+03:00");
  return Number.isNaN(d.getTime()) ? null : d;
}


function _parts(d: Date, withSeconds: boolean): Record<string, string> {
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: MSK_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    ...(withSeconds ? { second: "2-digit" } : {}),
    hour12: false,
  });
  const out: Record<string, string> = {};
  for (const p of fmt.formatToParts(d)) {
    if (p.type !== "literal") out[p.type] = p.value;
  }
  // Some Chromium versions render the midnight hour as "24" instead of
  // "00" when hour12=false. Normalise.
  if (out.hour === "24") out.hour = "00";
  return out;
}


/** `2026-05-07 15:30` in Moscow time, or `—` for null/invalid. */
export function formatMsk(iso: string | null | undefined): string {
  const d = _parse(iso);
  if (!d) return iso || "—";
  const p = _parts(d, false);
  return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}`;
}


/** `2026-05-07 15:30:42` in Moscow time. */
export function formatMskFull(iso: string | null | undefined): string {
  const d = _parse(iso);
  if (!d) return iso || "—";
  const p = _parts(d, true);
  return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}:${p.second}`;
}


/** `2026-05-07` (date only) in Moscow time. */
export function formatMskDate(iso: string | null | undefined): string {
  const d = _parse(iso);
  if (!d) return iso || "—";
  const p = _parts(d, false);
  return `${p.year}-${p.month}-${p.day}`;
}


/** `15:30` (time only) in Moscow time. */
export function formatMskTime(iso: string | null | undefined): string {
  const d = _parse(iso);
  if (!d) return iso || "—";
  const p = _parts(d, false);
  return `${p.hour}:${p.minute}`;
}
